import isEqual from 'lodash/isEqual';
import { ReactNode, useEffect, useRef } from 'react';
import { useProcessStoreUpdate } from './process-store';
import type { ProcessScope } from './process-store.type';

interface ProcessStoreProviderProps {
  children: ReactNode;
  initialScope?: Partial<ProcessScope>;
  initialRightScope?: Partial<ProcessScope>;
  isComparisonView?: boolean;
}

/**
 * Provider component that initializes the process store with values from props
 * This mimics the behavior of the ProcessProvider component from the context API
 */
export const ProcessStoreProvider = ({
  children,
  initialScope,
  initialRightScope,
  isComparisonView = false,
}: ProcessStoreProviderProps) => {
  const { setScope, setRightScope, setIsComparisonView } = useProcessStoreUpdate();

  // Using refs to store previous values for comparison
  const prevInitialScopeRef = useRef(initialScope);
  const prevInitialRightScopeRef = useRef(initialRightScope);
  const prevIsComparisonViewRef = useRef(isComparisonView);

  // Initialize the store with the provided values
  // biome-ignore lint/correctness/useExhaustiveDependencies(setScope): ignore
  useEffect(() => {
    // Only update if initialScope has actually changed
    if (initialScope && !isEqual(initialScope, prevInitialScopeRef.current)) {
      setScope(initialScope);
      prevInitialScopeRef.current = initialScope;
    }
  }, [initialScope]);

  // Initialize the right section for comparison view
  // biome-ignore lint/correctness/useExhaustiveDependencies(setRightScope): ignore
  useEffect(() => {
    // Only update if initialRightScope has actually changed
    if (initialRightScope && !isEqual(initialRightScope, prevInitialRightScopeRef.current)) {
      setRightScope(initialRightScope);
      prevInitialRightScopeRef.current = initialRightScope;
    }
  }, [initialRightScope]);

  // Set comparison mode
  // biome-ignore lint/correctness/useExhaustiveDependencies(setIsComparisonView): ignore
  useEffect(() => {
    // Only update if isComparisonView has actually changed
    if (isComparisonView !== prevIsComparisonViewRef.current) {
      setIsComparisonView(isComparisonView);
      prevIsComparisonViewRef.current = isComparisonView;
    }
  }, [isComparisonView]);

  return <>{children}</>;
};
